import { BillingDetails } from "store/exhibitors/cartStepper/helper";
import { Timestamp } from "api/firebase";
import {
  IExhibitorUserConnection,
  IStand,
  ISupplierCompany,
  ISurcharge,
} from "@evennt-tech/api-v2-core";

import {
  ExtraProduct,
  ProductImage,
  ProductNotes,
  QuickOrderProductType,
} from "./product";
import Transfer from "./transfer";
import { PaymentMethodType } from "./paymentMethod";
import FinancialAddresses from "./financialAddresses";
import Fee from "./fee";
import OrderReview from "./orderReview";

export enum OrderProductStatus {
  reviewProcess = "reviewProcess",
  received = "received",
  processing = "processing",
  complete = "complete",
  inTransit = "inTransit",
  cancelled = "cancelled",
  delivered = "delivered",
}

export enum OrderStatus {
  draft = "draft",
  reviewProcess = "reviewProcess",
  reviewCompleted = "reviewCompleted",
  processing = "processing",
  awaitingPayment = "awaitingPayment",
  cancelled = "cancelled",
  paid = "paid",
}

export type PaymentStatusBucket =
  | "paid"
  | "notPaid"
  | "preAuthorised"
  | "overdue";

export type OrderProductOwner = {
  id: string;
  companyName: string;
  organiserFee: number;
  contactPersonId: string;
  logo: string;
};

// TODO: Move to itself file. Improve order product saving
export type InvoiceStatus = "draft" | "open" | "paid";

export interface Invoice {
  id: string;
  status: InvoiceStatus;
  invoiceId?: string;
  transfers: Transfer[];
  createdAt: Timestamp;
  dueDate?: Timestamp;
  poNumber?: string;
  subtotalAmount: number;
  paymentFeeAmount: number | null;
  platformFeeAmount?: number;
  discountAmount?: number;
  taxAmount: number;
  totalAmount: number;
  amountPaid?: number;
  invoiceNumber?: string;
  products: OrderProduct[];
  stripeId?: string;
}
type OrderProductCpoConfigItem = {
  id: string;
  itemToFiles?: string[];
  name: string;
  cpoTitle: string;
  optionId?: string;
  isCustom: boolean;
};
export type OrderProductCpoConfig = {
  id: string;
  priceId: string;
  items: OrderProductCpoConfigItem[];
  isCustom: boolean;
  configToFiles?: string[];
};
export type OrderProductDiscount = {
  id: string;
  ruleId: string;
  expirationId: string;
  amount: number;
  amountWithFees: number;
  extras: { id: string; amount: number; amountWithFees: number }[];
};

export type OrderProductSubCategories = {
  [mainCategoryName: string]: {
    subCategoryNames: string[];
  };
};

export type OrderProduct = {
  id: string;
  name: string;
  quantity: number;
  price: number;
  priceWithFees: number;
  status: OrderProductStatus;
  categories: string[];
  subCategories: OrderProductSubCategories;
  surcharge?: ISurcharge[] | null;
  paymentFeeAmount?: number | null;
  platformFeeAmount?: number;
  approval?: boolean;
  image?: ProductImage;
  extras?: ExtraProduct[];
  fees?: Fee[];
  owner: OrderProductOwner;
  invoiceId?: string;
  notes?: ProductNotes;
  projectCode?: string;
  cpoConfig?: OrderProductCpoConfig;
  discountAmount?: number;
  discountAmountWithFees?: number;
  discountCodes?: OrderProductDiscount[];
  type?: QuickOrderProductType;
  productId?: string;
};

interface Order {
  id: string;
  /**
   * @deprecated connections.exhibitorUserId
   */
  exhibitorId: string;
  exhibitorPairId?: string;
  useNewProcess?: boolean;
  exhibitorCompanyId?: string;
  standId: string;
  organiserRevenue: number;
  review?: OrderReview;
  billingDetails: BillingDetails;
  cancelReason?: { [k: string]: string };
  cancelledAt?: Timestamp;
  financialAddresses?: FinancialAddresses | FinancialAddresses[];
  paymentDetails: {
    status: OrderStatus;
    amount: number;
    paymentDate?: Timestamp;
  };
  products: OrderProduct[];
  createdAt: Timestamp;
  isPartiallyCancelled?: boolean;
  isQuick?: boolean;
  createdViaSupplierInvite?: boolean;
  allowBankTransfer?: boolean;
  orderGroupId: string;
}
export interface NotSelectedOrder extends Order {
  paymentType: PaymentMethodType.notSelected;
}
export interface CardOrder extends Order {
  paymentType: PaymentMethodType.cardPayment;
  transfers: Transfer[];
  paymentIntentId?: string;
}
export interface IdealOrder extends Order {
  paymentType: PaymentMethodType.idealPayment;
  transfers: Transfer[];
  paymentIntentId?: string;
}

export type OrderInvoices = { [k: string]: Invoice };

export interface BankTransferOrder extends Order {
  paymentType: PaymentMethodType.bankTransfer;
  invoices: OrderInvoices;
}

export type RawOrder =
  | NotSelectedOrder
  | CardOrder
  | BankTransferOrder
  | IdealOrder;

export type ExpandedOrder = RawOrder & {
  exhibitorConnection?: IExhibitorUserConnection;
  supplierCompanies?: ISupplierCompany[];
  stand?: IStand | null;
  paymentStatusBuckets?: PaymentStatusBucket[];
};

export default RawOrder;
